import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  CircularProgress,
  CssBaseline,
  PaletteMode,
  Snackbar,
  ThemeProvider
} from "@mui/material";
import { Maybe } from "graphql/jsutils/Maybe";
import { useContext, useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { ParallaxProvider } from "react-scroll-parallax";
import Footer from "./components/layout/Footer";
import Header from "./components/layout/Header";
import ScrollPositionRestore from "./components/utils/ScrollPositionRestore";
import { COLOR_MODE, CONTENTFUL_DATA, LANGUAGE } from "./constants/global";
import { routeId, routes } from "./constants/routes";
import { ColorContext } from "./context/ColorContext";
import { ContentfulContext } from "./context/ContentfulContext";
import { ErrorContext, ErrorContextSchema } from "./context/ErrorContext";
import { LanguageContext, LanguageContextSchema } from "./context/LanguageContext";
import { Language, LanguageCollection } from "./models/schema";
import AboutUsPage from "./pages/AboutUsPage";
import ContactPage from "./pages/ContactPage";
import HomePage from "./pages/HomePage";
import LegalInformationPage from "./pages/LegalInformationPage";
import ProjectPage from "./pages/ProjectPage";
import ReferencesPage from "./pages/ReferencesPage";
import SolutionsPage from "./pages/SolutionsPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import {
  GET_LANGUAGES,
  GET_LANGUAGE_CONTENT_PART_1,
  GET_LANGUAGE_CONTENT_PART_2,
  GET_LANGUAGE_CONTENT_PART_3
} from "./querries/querries";
import { darkTheme } from "./theme/dark";
import { lightTheme } from "./theme/light";
import { getItem, setItem } from "./utils/storage";

const Content = ({ language }: { language: string }) => {
  const { data: languageContentPart1, loading: loadingPart1 } = useQuery<{
    languageCollection: LanguageCollection;
  }>(GET_LANGUAGE_CONTENT_PART_1, {
    variables: {
      code: language
    },
    fetchPolicy: "no-cache"
  });

  const { data: languageContentPart2, loading: loadingPart2 } = useQuery<{
    languageCollection: LanguageCollection;
  }>(GET_LANGUAGE_CONTENT_PART_2, {
    variables: {
      code: language
    },
    fetchPolicy: "no-cache"
  });

  const { data: languageContentPart3, loading: loadingPart3 } = useQuery<{
    languageCollection: LanguageCollection;
  }>(GET_LANGUAGE_CONTENT_PART_3, {
    variables: {
      code: language
    },
    fetchPolicy: "no-cache"
  });

  const key = useMemo(() => `${CONTENTFUL_DATA}_${language}`, [language]);
  const [data, setData] = useState<Maybe<Language>>(getItem(key));

  useEffect(() => {
    if (languageContentPart1 && languageContentPart2 && languageContentPart3) {
      const languageContentMerged = {
        ...languageContentPart1?.languageCollection?.items[0],
        ...languageContentPart2?.languageCollection?.items[0],
        ...languageContentPart3?.languageCollection?.items[0]
      };
      setData(languageContentMerged as Language);
    }
  }, [languageContentPart1, languageContentPart2, languageContentPart3]);

  useEffect(() => {
    if (data) {
      setItem(key, data);
    }
  }, [data, key]);

  const { error, setError } = useContext(ErrorContext);

  if (loadingPart1 || loadingPart2 || loadingPart3) {
    <Box
      sx={{
        width: 1,
        height: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}>
      <CircularProgress size={100} />
    </Box>;
  }

  return (
    <>
      {data && (
        <ContentfulContext.Provider value={{ content: data as Language }}>
          <Router>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minHeight: "100%",
                px: "0 !important",
                mx: "0 !important",
                overflowX: { xs: "hidden", md: "unset" }
              }}>
              <Header />
              <Box
                sx={{
                  flex: "1",
                  flexDirection: "column",
                  display: "flex"
                }}>
                <ScrollPositionRestore />
                <Snackbar
                  open={!!error}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                  autoHideDuration={6000}
                  onClose={() => setError(null)}>
                  <Alert
                    onClose={() => setError(null)}
                    severity="error"
                    sx={{
                      display: "flex",
                      alignItems: "center"
                    }}>
                    {data?.errorMessage as string}
                  </Alert>
                </Snackbar>
                <Routes>
                  <Route path={routes.home} element={<HomePage />}></Route>
                  <Route path={routes.solutions} element={<SolutionsPage />}></Route>
                  <Route path={routes.project + routeId} element={<ProjectPage />}></Route>
                  <Route path={routes.references} element={<ReferencesPage />}></Route>
                  <Route path={routes.aboutUs} element={<AboutUsPage />}></Route>
                  <Route path={routes.contact} element={<ContactPage />}></Route>
                  <Route path={routes.legal} element={<LegalInformationPage />}></Route>
                  <Route path={routes.termsOfUse} element={<TermsOfUsePage />}></Route>

                  {/* Error handling */}
                  <Route path="*" element={<Navigate to={routes.home} replace />} />
                </Routes>
              </Box>
              <Footer />
            </Box>
          </Router>
        </ContentfulContext.Provider>
      )}
    </>
  );
};

const App = () => {
  // Theme

  const storedMode = getItem(COLOR_MODE) as PaletteMode;
  const [colorMode, setColorMode] = useState<PaletteMode>(storedMode || ("light" as PaletteMode));
  const theme = useMemo(() => (colorMode === "light" ? lightTheme : darkTheme), [colorMode]);

  const selectedColorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setColorMode((prevMode: PaletteMode) => (prevMode === "light" ? "dark" : "light"));
      }
    }),
    []
  );

  useEffect(() => {
    setItem(COLOR_MODE, colorMode);
  }, [colorMode]);

  // Contentful

  const { data: languageCollection } = useQuery<{
    languageCollection: LanguageCollection;
  }>(GET_LANGUAGES);

  const storedLanguage = getItem(LANGUAGE);
  const [language, setLanguage] = useState<string | null>(storedLanguage);

  const languageContextData = useMemo(
    () =>
      ({
        selectedLanguage: language,
        availableLanguages: languageCollection?.languageCollection?.items?.filter(
          (lang) => !!lang?.active
        ) as Language[],
        setLanguage: (lang: string) => {
          setLanguage(lang);
        }
      } as LanguageContextSchema),
    [language, languageCollection?.languageCollection?.items]
  );

  useEffect(() => {
    if (!language) {
      const defaultLang =
        (languageCollection?.languageCollection?.items.find((lang) => lang?.code === "sk")
          ?.code as string) || (languageCollection?.languageCollection?.items[0]?.code as string);
      setLanguage(defaultLang);
    }
  }, [languageCollection, language]);

  useEffect(() => {
    if (language) {
      setItem(LANGUAGE, language);
    }
  }, [language]);

  // Errors

  const [error, setError] = useState<string | null>();

  const errorContextData = useMemo(
    () =>
      ({
        error,
        setError
      } as ErrorContextSchema),
    [error]
  );

  // Recaptha

  // const ref = useRef<ReCAPTCHA | null>(null);
  // const [neccessaryCookiesAllowed, setNeccessaryCookiesAllowed] = useState<boolean>(
  //   getItem(NECESSARY_COOKIES)
  // );
  // const [badgeVisible, setBadgeVisible] = useState<boolean>(false);
  // const [recaptchaRef, setRecaptchaRef] = useState<MutableRefObject<ReCAPTCHA | null> | null>(null);

  // const recaptchaContextData = useMemo(
  //   () =>
  //     ({
  //       badgeVisible,
  //       recaptchaRef,
  //       showBadge: () => setBadgeVisible(true),
  //       hideBadge: () => setBadgeVisible(false),
  //       neccessaryCookiesAllowed,
  //       setNeccessaryCookiesAllowed
  //     } as RecaptchaContextSchema),
  //   [badgeVisible, recaptchaRef, neccessaryCookiesAllowed]
  // );

  // useEffect(() => {
  //   if (ref) {
  //     setRecaptchaRef(ref);
  //   }
  // }, [ref]);

  return (
    <ColorContext.Provider value={selectedColorMode}>
      <LanguageContext.Provider value={languageContextData}>
        <ErrorContext.Provider value={errorContextData}>
          <ThemeProvider theme={theme}>
            <ParallaxProvider>
              {/* <RecaptchaContext.Provider value={recaptchaContextData}> */}
              <CssBaseline enableColorScheme />
              {language && <Content language={language} />}
              {/* <GoogleRecaptcha recaptchaRef={ref} />
                  </RecaptchaContext.Provider> */}
            </ParallaxProvider>
          </ThemeProvider>
        </ErrorContext.Provider>
      </LanguageContext.Provider>
    </ColorContext.Provider>
  );
};

export default App;
